// frontend_react/src/context/CustomerPortalContext.jsx

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import {
  customerLogin,
  fetchCurrentCustomer,
  updateCurrentCustomer,
} from "../services/customerPortalApi";

import { logout as apiLogout } from "../services/authApi";
import logger from "../utils/logger";

const CustomerPortalContext = createContext(null);

export function CustomerPortalProvider({ children }) {
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const normalizeCustomer = useCallback((data) => {
    if (!data) return null;

    return {
      ...data,
      uuid: data.uuid || data.client_uuid || null,
      firstname: data.firstname || data.first_name || "",
      lastname: data.lastname || data.last_name || "",
      email: data.email || "",
      phone_number: data.phone_number || data.phone || "",
      city: data.city || "",
      loyalty_points: data.loyalty_points ?? 0,
      authenticated: true,
    };
  }, []);

  const clearAuthStorage = useCallback(() => {
    localStorage.removeItem("csrf_access_token");
    localStorage.removeItem("csrf_refresh_token");
  }, []);

  const fetchCustomer = useCallback(async () => {
    setAuthLoading(true);
    setError("");

    try {
      const { data } = await fetchCurrentCustomer();
      const normalized = normalizeCustomer(data);
      setCustomer(normalized);
      return normalized;
    } catch (err) {
      logger.warn("Customer fetch failed", err);
      setCustomer(null);
      return null;
    } finally {
      setAuthLoading(false);
    }
  }, [normalizeCustomer]);

  const login = useCallback(
    async ({ username, email, password }) => {
      setLoading(true);
      setError("");
      setSuccess("");

      try {
        await customerLogin({ username, email, password });
        const connectedCustomer = await fetchCustomer();

        setSuccess("Connexion client réussie");

        if (connectedCustomer) {
          navigate("/customer", { replace: true });
        }

        return connectedCustomer;
      } catch (err) {
        const message =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.response?.data?.msg ||
          "Échec de la connexion client";

        setError(message);
        setCustomer(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [fetchCustomer, navigate]
  );

  const updateProfile = useCallback(async (payload) => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const { data } = await updateCurrentCustomer(payload);
      const normalized = normalizeCustomer(data);
      setCustomer(normalized);
      setSuccess("Profil client mis à jour.");
      return normalized;
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.msg ||
        "Impossible de mettre à jour le profil client.";

      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [normalizeCustomer]);

  const logout = useCallback(async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await apiLogout();
    } catch (err) {
      logger.error("Erreur logout client :", err);
    } finally {
      clearAuthStorage();
      setCustomer(null);
      setLoading(false);
      setAuthLoading(false);
      navigate("/", { replace: true });
    }
  }, [clearAuthStorage, navigate]);

  const value = useMemo(
    () => ({
      customer,
      currentCustomer: customer,
      isCustomerAuthenticated: !!customer,
      loading,
      authLoading,
      error,
      success,
      login,
      logout,
      fetchCustomer,
      updateProfile,
      setError,
      setSuccess,
    }),
    [
      customer,
      loading,
      authLoading,
      error,
      success,
      login,
      logout,
      fetchCustomer,
      updateProfile,
    ]
  );

  return (
    <CustomerPortalContext.Provider value={value}>
      {children}
    </CustomerPortalContext.Provider>
  );
}

CustomerPortalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useCustomerPortal() {
  const context = useContext(CustomerPortalContext);

  if (!context) {
    throw new Error(
      "useCustomerPortal must be used within a CustomerPortalProvider"
    );
  }

  return context;
}
